import { useEffect, useRef, useState } from "react";

/**
 * useReveal — fires a one-way "revealed" flag the first time an element scrolls
 * into view, powering scroll-triggered entrance animations without any external
 * dependency (built on the native IntersectionObserver).
 *
 * Accessibility: when the user has `prefers-reduced-motion: reduce` set — or the
 * browser lacks IntersectionObserver — the element is revealed immediately so no
 * content is ever hidden behind an animation that won't play.
 *
 * @param {Object}  [options]
 * @param {number}  [options.threshold=0.15]   Fraction of the element that must
 *                                             be visible before it reveals.
 * @param {string}  [options.rootMargin]       IntersectionObserver rootMargin —
 *                                             defaults to triggering slightly
 *                                             before the element hits the fold.
 * @param {boolean} [options.once=true]        Reveal only once (stays visible).
 * @returns {[React.RefObject, boolean]} `[ref, isVisible]`
 */
export default function useReveal({
  threshold = 0.15,
  rootMargin = "0px 0px -10% 0px",
  once = true,
} = {}) {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;

    const reduceMotion =
      typeof window !== "undefined" &&
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (reduceMotion || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            if (once) observer.unobserve(entry.target);
          } else if (!once) {
            setIsVisible(false);
          }
        });
      },
      { threshold, rootMargin }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [threshold, rootMargin, once]);

  return [ref, isVisible];
}
